import createClient from "openapi-fetch";

import type { paths } from "@/api/generated/schema";
import { ADMIN_REFRESH_STORAGE_KEY } from "@/api/constants";
import { postRefreshToken, writeRefreshToken } from "@/api/refreshToken";
import { useAuthStore } from "@/stores/authStore";
import { getAdminApiBase } from "@/utils/env";

const nativeFetch = globalThis.fetch.bind(globalThis);

const fetchWithSilentRefresh: typeof fetch = async (input, init) => {
  const request = input instanceof Request ? input : new Request(input, init);

  const response = await nativeFetch(request);
  if (response.status !== 401) return response;

  const url = request.url;
  if (
    url.includes("/admin/v1/auth/login") ||
    url.includes("/admin/v1/auth/refresh")
  ) {
    return response;
  }

  if (request.headers.get("x-admin-auth-retry") === "1") {
    return response;
  }

  const refresh =
    typeof window !== "undefined"
      ? sessionStorage.getItem(ADMIN_REFRESH_STORAGE_KEY)
      : null;
  if (!refresh) return response;

  const pair = await postRefreshToken(refresh);
  if (!pair) {
    useAuthStore.getState().clearSession();
    window.dispatchEvent(new Event("admin:session-ended"));
    return response;
  }
  writeRefreshToken(pair.refresh_token);
  useAuthStore.getState().setAccessToken(pair.access_token);

  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${pair.access_token}`);
  headers.set("x-admin-auth-retry", "1");
  const replay = new Request(request, { headers });
  return nativeFetch(replay);
};

/** 统一管理端 openapi-fetch 客户端（401 单次静默 refresh + 重放） */
export const adminApi = createClient<paths>({
  baseUrl: getAdminApiBase(),
  fetch: fetchWithSilentRefresh,
});

adminApi.use({
  onRequest({ request }) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return undefined;
  },
});
